import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getMediaUrl } from '../services/api';
import { Flight } from '../store/flights';

interface LLMTemplate {
  id: string;
  display_id: string;
  name: string;
  description: string | null;
  category: string;
  template_type: string;
  canvas: {
    width: number;
    height: number;
    format_key?: string;
  };
}

interface GeneratedPost {
  id: string;
  slide: number;
  content: Record<string, string>;
  image_url?: string;
  text?: string;
  status: 'pending' | 'generating' | 'ready' | 'error';
  error?: string;
}

interface BriefingFormData {
  template_id: string;
  product_name: string;
  product_description: string;
  target_audience: string;
  key_benefits: string;
  tone_of_voice: string;
  hashtags: string;
  post_count: number;
  platforms: string[];
  flight_id?: string;
}

interface SavedBriefing {
  id: string;
  display_id: string;
  name: string;
  briefing_data: {
    product_name: string;
    product_description?: string;
    target_audience?: string;
    key_benefits?: string;
    tone_of_voice?: string;
    hashtags?: string;
  };
  template_id: string | null;
  post_count: number;
  platforms: string[];
  usage_count: number;
  last_used_at: string | null;
}

interface AIBriefingPanelProps {
  projectId: string;
  flights?: Flight[];
  onPostsGenerated?: (posts: GeneratedPost[]) => void;
  onClose: () => void;
}

export function AIBriefingPanel({ projectId, flights = [], onPostsGenerated, onClose }: AIBriefingPanelProps) {
  const { t } = useTranslation(['posts', 'common']);
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<LLMTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<LLMTemplate | null>(null);
  const [step, setStep] = useState<'template' | 'briefing' | 'generating' | 'review'>('template');
  const [generatedPosts, setGeneratedPosts] = useState<GeneratedPost[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Saved briefings
  const [savedBriefings, setSavedBriefings] = useState<SavedBriefing[]>([]);
  const [showSavedBriefings, setShowSavedBriefings] = useState(false);
  const [briefingName, setBriefingName] = useState('');
  const [isSavingBriefing, setIsSavingBriefing] = useState(false);

  const [formData, setFormData] = useState<BriefingFormData>({
    template_id: '',
    product_name: '',
    product_description: '',
    target_audience: '',
    key_benefits: '',
    tone_of_voice: 'professionell, freundlich',
    hashtags: '',
    post_count: 5,
    platforms: ['instagram'],
    flight_id: undefined,
  });

  // Lade LLM-Templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await api.get<{ data: LLMTemplate[] }>(`/creator/llm-templates?project_id=${projectId}`);
        setTemplates(response.data || []);
      } catch (err) {
        console.error('Failed to load templates:', err);
        setError(t('posts:ai.templatesLoadError'));
      } finally {
        setIsLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, [projectId, t]);

  // Lade gespeicherte Briefings
  useEffect(() => {
    const fetchBriefings = async () => {
      try {
        const response = await api.get<{ data: SavedBriefing[] }>(`/creator/briefings?project_id=${projectId}`);
        setSavedBriefings(response.data || []);
      } catch (err) {
        console.error('Failed to load briefings:', err);
      }
    };
    fetchBriefings();
  }, [projectId]);

  // Flight-Kontext laden wenn ausgewählt
  useEffect(() => {
    if (formData.flight_id) {
      const flight = flights.find(f => f.id === formData.flight_id);
      if (flight?.ai_context) {
        setFormData(prev => ({
          ...prev,
          target_audience: flight.ai_context.target_audience || prev.target_audience,
          tone_of_voice: flight.ai_context.brand_voice || prev.tone_of_voice,
          hashtags: flight.ai_context.hashtag_strategy?.join(' ') || prev.hashtags,
        }));
      }
    }
  }, [formData.flight_id, flights]);

  // Briefing laden
  const handleLoadBriefing = async (briefing: SavedBriefing) => {
    // Formular mit gespeicherten Daten füllen
    setFormData({
      template_id: briefing.template_id || formData.template_id,
      product_name: briefing.briefing_data.product_name,
      product_description: briefing.briefing_data.product_description || '',
      target_audience: briefing.briefing_data.target_audience || '',
      key_benefits: briefing.briefing_data.key_benefits || '',
      tone_of_voice: briefing.briefing_data.tone_of_voice || 'professionell, freundlich',
      hashtags: briefing.briefing_data.hashtags || '',
      post_count: briefing.post_count,
      platforms: briefing.platforms,
      flight_id: formData.flight_id,
    });

    // Template auswählen wenn vorhanden
    if (briefing.template_id) {
      const template = templates.find(t => t.id === briefing.template_id || t.display_id === briefing.template_id);
      if (template) {
        setSelectedTemplate(template);
      }
    }

    // Usage Counter erhöhen
    try {
      await api.post(`/creator/briefings/${briefing.id}/use`, {});
    } catch (err) {
      console.error('Failed to mark briefing as used:', err);
    }

    setShowSavedBriefings(false);
    setBriefingName(briefing.name);
  };

  // Briefing speichern
  const handleSaveBriefing = async () => {
    if (!briefingName.trim()) {
      setError(t('posts:ai.briefingNameRequired'));
      return;
    }

    setIsSavingBriefing(true);
    try {
      const response = await api.post<{ data: SavedBriefing }>('/creator/briefings', {
        project_id: projectId,
        name: briefingName,
        briefing_data: {
          product_name: formData.product_name,
          product_description: formData.product_description,
          target_audience: formData.target_audience,
          key_benefits: formData.key_benefits,
          tone_of_voice: formData.tone_of_voice,
          hashtags: formData.hashtags,
        },
        template_id: selectedTemplate?.display_id,
        flight_id: formData.flight_id,
        post_count: formData.post_count,
        platforms: formData.platforms,
        generate_images: true,
      });

      setSavedBriefings(prev => [response.data, ...prev]);
      setError(null);
    } catch (err) {
      console.error('Failed to save briefing:', err);
      setError(t('posts:ai.briefingSaveError'));
    } finally {
      setIsSavingBriefing(false);
    }
  };

  // Briefing löschen
  const handleDeleteBriefing = async (briefingId: string) => {
    try {
      await api.delete(`/creator/briefings/${briefingId}`);
      setSavedBriefings(prev => prev.filter(b => b.id !== briefingId));
    } catch (err) {
      console.error('Failed to delete briefing:', err);
    }
  };

  const handleTemplateSelect = (template: LLMTemplate) => {
    setSelectedTemplate(template);
    setFormData(prev => ({ ...prev, template_id: template.id }));
    setStep('briefing');
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) return;

    setError(null);
    setStep('generating');
    setGenerationProgress(0);

    try {
      // Simuliere Progress während der Generierung
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      // Content-Sheet aus Briefing erstellen
      const contentSheet = Array.from({ length: formData.post_count }, (_, i) => ({
        slide: i + 1,
        content_id: String(i + 1),
        content: {
          product_name: formData.product_name,
          product_description: formData.product_description,
          headline: '', // Wird vom LLM gefüllt
          subline: '',
          cta_text: t('posts:ai.defaultCta'),
        },
      }));

      // Sende an Backend (n8n Webhook oder direkte API)
      const response = await api.post<{
        success: boolean;
        data: {
          posts: GeneratedPost[];
          generation_id?: string;
        };
        message?: string;
        ai_status?: {
          text_generation: string;
          image_generation: string;
        };
        async?: boolean;
      }>('/creator/ai-generate', {
        project_id: projectId,
        template_id: selectedTemplate.display_id,
        flight_id: formData.flight_id,
        briefing: {
          product_name: formData.product_name,
          product_description: formData.product_description,
          target_audience: formData.target_audience,
          key_benefits: formData.key_benefits,
          tone_of_voice: formData.tone_of_voice,
          hashtags: formData.hashtags,
        },
        post_count: formData.post_count,
        platforms: formData.platforms,
        content_sheet: contentSheet,
        generate_images: true, // Enable DALL-E image generation
      });

      clearInterval(progressInterval);
      setGenerationProgress(100);

      // Log AI status
      if (response.ai_status) {
        console.log('AI Status:', response.ai_status);
      }

      if (response.data?.posts && response.data.posts.length > 0) {
        setGeneratedPosts(response.data.posts);
        setStep('review');
        onPostsGenerated?.(response.data.posts);
      } else if (response.async) {
        // n8n is processing async - show waiting state
        setError(t('posts:ai.generatingInBackground'));
        // Could poll for results here
      } else {
        throw new Error(response.message || t('posts:ai.noPostsGenerated'));
      }
    } catch (err) {
      console.error('Generation failed:', err);
      setError(err instanceof Error ? err.message : t('posts:ai.generationFailed'));
      setStep('briefing');
    }
  };

  const handleSavePosts = async () => {
    try {
      // Posts als Drafts speichern
      await api.post('/posts/batch/save-generated', {
        project_id: projectId,
        flight_id: formData.flight_id,
        posts: generatedPosts.map(post => ({
          text: post.text || post.content?.headline || formData.product_name,
          platforms: formData.platforms,
          post_type: 'reel',
          status: 'draft',
          media_urls: post.image_url ? [post.image_url] : [],
        })),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('posts:ai.saveFailed'));
    }
  };

  const handleExportLocal = () => {
    const exportData = {
      generated_at: new Date().toISOString(),
      template: selectedTemplate?.name,
      briefing: formData,
      posts: generatedPosts,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-content-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold">{t('posts:ai.title')}</h2>
              <p className="text-sm text-white/80">
                {step === 'template' && t('posts:ai.steps.template')}
                {step === 'briefing' && t('posts:ai.steps.briefing')}
                {step === 'generating' && t('posts:ai.steps.generating')}
                {step === 'review' && t('posts:ai.steps.review')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center px-6 py-3 bg-gray-50 border-b">
          {['template', 'briefing', 'generating', 'review'].map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition ${
                step === s ? 'bg-purple-600 text-white' :
                ['template', 'briefing', 'generating', 'review'].indexOf(step) > i ? 'bg-green-500 text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {['template', 'briefing', 'generating', 'review'].indexOf(step) > i ? '✓' : i + 1}
              </div>
              {i < 3 && <div className={`w-12 h-0.5 mx-2 ${
                ['template', 'briefing', 'generating', 'review'].indexOf(step) > i ? 'bg-green-500' : 'bg-gray-200'
              }`} />}
            </div>
          ))}
          <div className="ml-4 text-sm text-gray-500">
            {step === 'template' && `1. ${t('posts:ai.steps.template')}`}
            {step === 'briefing' && `2. ${t('posts:ai.steps.briefing')}`}
            {step === 'generating' && `3. ${t('posts:ai.steps.generating')}`}
            {step === 'review' && `4. ${t('posts:ai.steps.review')}`}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
              <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">×</button>
            </div>
          )}

          {/* Step 1: Template Selection */}
          {step === 'template' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('posts:ai.selectTemplate')}</h3>
              {isLoadingTemplates ? (
                <div className="text-center py-12 text-gray-500">{t('posts:ai.loadingTemplates')}</div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 mb-2">{t('posts:ai.noTemplates')}</p>
                  <p className="text-sm text-gray-400">{t('posts:ai.noTemplatesHint')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {templates.map(template => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`p-4 border-2 rounded-xl text-left transition hover:border-purple-300 hover:bg-purple-50 ${
                        selectedTemplate?.id === template.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="w-full h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg mb-3 flex items-center justify-center">
                        <span className="text-2xl">
                          {template.category === 'story' ? '📱' : template.category === 'post' ? '📷' : '🎬'}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-900 truncate">{template.name}</h4>
                      <p className="text-xs text-gray-500 mt-1">
                        {template.canvas.width}x{template.canvas.height} • {template.category}
                      </p>
                      <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                        {template.display_id}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Briefing Form */}
          {step === 'briefing' && selectedTemplate && (
            <div className="max-w-2xl mx-auto">
              {/* Template Info + Saved Briefings */}
              <div className="flex items-center gap-3 mb-4 p-4 bg-purple-50 rounded-lg">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-xl">
                  {selectedTemplate.category === 'story' ? '📱' : selectedTemplate.category === 'post' ? '📷' : '🎬'}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{selectedTemplate.name}</h4>
                  <p className="text-sm text-gray-500">{selectedTemplate.display_id}</p>
                </div>
                <button
                  onClick={() => setStep('template')}
                  className="ml-auto text-sm text-purple-600 hover:text-purple-700"
                >
                  {t('common:actions.change')}
                </button>
              </div>

              {/* Saved Briefings Section */}
              {savedBriefings.length > 0 && (
                <div className="mb-4">
                  <button
                    onClick={() => setShowSavedBriefings(!showSavedBriefings)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600"
                  >
                    <svg className={`w-4 h-4 transition-transform ${showSavedBriefings ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    {t('posts:ai.savedBriefings', { count: savedBriefings.length })}
                  </button>
                  {showSavedBriefings && (
                    <div className="mt-2 border rounded-lg divide-y max-h-48 overflow-y-auto">
                      {savedBriefings.map(briefing => (
                        <div
                          key={briefing.id}
                          className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleLoadBriefing(briefing)}
                        >
                          <div>
                            <div className="font-medium text-sm text-gray-900">{briefing.name}</div>
                            <div className="text-xs text-gray-500">
                              {briefing.briefing_data.product_name} • {t('posts:ai.usageCount', { count: briefing.usage_count })}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(t('posts:ai.confirmDeleteBriefing'))) {
                                handleDeleteBriefing(briefing.id);
                              }
                            }}
                            className="p-1 text-gray-400 hover:text-red-500"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Save Briefing Row */}
              <div className="mb-4 flex items-center gap-2">
                <input
                  type="text"
                  value={briefingName}
                  onChange={(e) => setBriefingName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder={t('posts:ai.briefingNamePlaceholder')}
                />
                <button
                  onClick={handleSaveBriefing}
                  disabled={!briefingName.trim() || !formData.product_name || isSavingBriefing}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1"
                >
                  {isSavingBriefing ? (
                    <span className="animate-spin">⏳</span>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                  )}
                  {t('common:actions.save')}
                </button>
              </div>

              <div className="space-y-4">
                {/* Flight Selection */}
                {flights.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('posts:ai.flightOptional')}
                    </label>
                    <select
                      value={formData.flight_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, flight_id: e.target.value || undefined }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">{t('posts:ai.noFlight')}</option>
                      {flights.filter(f => f.status === 'active' || f.status === 'draft').map(flight => (
                        <option key={flight.id} value={flight.id}>
                          {flight.name} ({flight.display_id})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('posts:ai.flightContextHint')}
                    </p>
                  </div>
                )}

                {/* Product Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('posts:ai.productName')} *
                    </label>
                    <input
                      type="text"
                      value={formData.product_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder={t('posts:ai.productNamePlaceholder')}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('posts:ai.postCount')}
                    </label>
                    <input
                      type="number"
                      value={formData.post_count}
                      onChange={(e) => setFormData(prev => ({ ...prev, post_count: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      min="1"
                      max="20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('posts:ai.description')}
                  </label>
                  <textarea
                    value={formData.product_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, product_description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    rows={2}
                    placeholder={t('posts:ai.descriptionPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('posts:ai.targetAudience')}
                  </label>
                  <input
                    type="text"
                    value={formData.target_audience}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_audience: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder={t('posts:ai.targetAudiencePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('posts:ai.keyBenefits')}
                  </label>
                  <textarea
                    value={formData.key_benefits}
                    onChange={(e) => setFormData(prev => ({ ...prev, key_benefits: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    rows={2}
                    placeholder={t('posts:ai.keyBenefitsPlaceholder')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('posts:ai.toneOfVoice')}
                    </label>
                    <input
                      type="text"
                      value={formData.tone_of_voice}
                      onChange={(e) => setFormData(prev => ({ ...prev, tone_of_voice: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder={t('posts:ai.toneOfVoicePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('posts:ai.hashtags')}
                    </label>
                    <input
                      type="text"
                      value={formData.hashtags}
                      onChange={(e) => setFormData(prev => ({ ...prev, hashtags: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="#ki #productivity #tipps"
                    />
                  </div>
                </div>

                {/* Platforms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('posts:ai.platforms')}
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.platforms.includes('instagram')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({ ...prev, platforms: [...prev.platforms, 'instagram'] }));
                          } else {
                            setFormData(prev => ({ ...prev, platforms: prev.platforms.filter(p => p !== 'instagram') }));
                          }
                        }}
                        className="mr-2 h-4 w-4 text-pink-600 rounded"
                      />
                      <span className="flex items-center gap-1 text-sm">
                        <svg className="w-4 h-4 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                        </svg>
                        Instagram
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.platforms.includes('facebook')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({ ...prev, platforms: [...prev.platforms, 'facebook'] }));
                          } else {
                            setFormData(prev => ({ ...prev, platforms: prev.platforms.filter(p => p !== 'facebook') }));
                          }
                        }}
                        className="mr-2 h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="flex items-center gap-1 text-sm">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                        Facebook
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Generating */}
          {step === 'generating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 mb-6 relative">
                <div className="absolute inset-0 rounded-full border-4 border-purple-200"></div>
                <div
                  className="absolute inset-0 rounded-full border-4 border-purple-600 border-t-transparent animate-spin"
                  style={{ animationDuration: '1s' }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl">🤖</span>
                </div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('posts:ai.generatingContent')}</h3>
              <p className="text-gray-500 mb-4">
                {t('posts:ai.generatingCount', { count: formData.post_count })}
              </p>
              <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-600 transition-all duration-300"
                  style={{ width: `${generationProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-400 mt-2">{generationProgress}%</p>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 'review' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('posts:ai.generatedCount', { count: generatedPosts.length })}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleExportLocal}
                    className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {t('posts:ai.exportJson')}
                  </button>
                  <button
                    onClick={() => setStep('briefing')}
                    className="px-3 py-1.5 text-sm text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50"
                  >
                    {t('posts:ai.regenerate')}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {generatedPosts.map((post, index) => (
                  <div key={post.id || index} className="border rounded-lg overflow-hidden bg-white shadow-sm">
                    {post.image_url ? (
                      <img
                        src={getMediaUrl(post.image_url)}
                        alt={`Post ${index + 1}`}
                        className="w-full h-40 object-cover"
                      />
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                        <span className="text-4xl">📱</span>
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500">Post {index + 1}</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          post.status === 'ready' ? 'bg-green-100 text-green-700' :
                          post.status === 'error' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {post.status === 'ready' ? t('posts:ai.statusReady') : post.status === 'error' ? t('posts:ai.statusError') : t('posts:ai.statusPending')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {post.text || post.content?.headline || t('posts:ai.noText')}
                      </p>
                      {post.error && (
                        <p className="text-xs text-red-500 mt-1">{post.error}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={step === 'template' ? onClose : () => setStep(step === 'briefing' ? 'template' : 'briefing')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {step === 'template' ? t('common:actions.cancel') : t('common:actions.back')}
          </button>

          {step === 'briefing' && (
            <button
              onClick={handleGenerate}
              disabled={!formData.product_name || formData.platforms.length === 0}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {t('posts:ai.generateContent')}
            </button>
          )}

          {step === 'review' && (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  onClose();
                  navigate(`/projects/${projectId}/ai-review`);
                }}
                className="px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                {t('posts:ai.goToReview')}
              </button>
              <button
                onClick={handleSavePosts}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('posts:ai.saveAsDrafts')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
